import { motion } from 'framer-motion'
import { useState } from 'react'
import { AlertCircle, TrendingUp, Sparkles, CreditCard, BarChart3, Target, Wallet, PiggyBank, Flame, Award, Heart, Gift } from 'lucide-react'
import PhoneMockup from './PhoneMockup'
import ChartPhoneMockup from './ChartPhoneMockup'
import TipsPhoneMockup from './TipsPhoneMockup'
import RoastPhoneMockup from './RoastPhoneMockup'
import HabitsScorePhoneMockup from './HabitsScorePhoneMockup'
import HealthCheckPhoneMockup from './HealthCheckPhoneMockup'
import PointsPhoneMockup from './PointsPhoneMockup'
import DispoDetailsModal from './DispoDetailsModal'
import ChartsDetailsModal from './ChartsDetailsModal'
import AITipsDetailsModal from './AITipsDetailsModal'
import RoastModeDetailsModal from './RoastModeDetailsModal'
import HabitsScoreDetailsModal from './HabitsScoreDetailsModal'
import CheckInDetailsModal from './CheckInDetailsModal'
import PointsDetailsModal from './PointsDetailsModal'
import { useLanguage } from '../contexts/LanguageContext'

const AppShowcaseSections = () => {
  const { t } = useLanguage()
  const [isDispoModalOpen, setIsDispoModalOpen] = useState(false)
  const [isChartsModalOpen, setIsChartsModalOpen] = useState(false)
  const [isAITipsModalOpen, setIsAITipsModalOpen] = useState(false)
  const [isRoastModalOpen, setIsRoastModalOpen] = useState(false)
  const [isHabitsModalOpen, setIsHabitsModalOpen] = useState(false)
  const [isCheckInModalOpen, setIsCheckInModalOpen] = useState(false)
  const [isPointsModalOpen, setIsPointsModalOpen] = useState(false)
  
  return (
    <>
      {/* Section Header */}
      <section id="app-showcase" className="py-12 lg:py-16 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-4"
          >
            <motion.div
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: "spring", duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6"
            >
              <Sparkles className="h-5 w-5 text-purple-400" />
              <span className="text-purple-400 font-semibold text-sm uppercase tracking-wider">
                {t('nav.powerfulFeatures')}
              </span>
            </motion.div>

            <h2 className="text-5xl font-bold mb-4">
              <span className="text-gradient">{t('features.titleGradient')}</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {t('features.description')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section 1: Dispo Prevention */}
      <section className="py-12 lg:py-16 bg-black overflow-hidden relative">
        {/* Gradient orb */}
        <div className="absolute top-1/2 left-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(239,68,68,0.1) 0%, transparent 60%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
                <AlertCircle className="h-4 w-4 text-red-400" />
                <span className="text-gray-300 text-sm">{t('showcase.dispo.badge')}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {t('showcase.dispo.title')}
                <br />
                <span className="bg-gradient-to-r from-red-400 via-orange-400 to-yellow-400 bg-clip-text text-transparent">
                  {t('showcase.dispo.titleGradient')}
                </span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('showcase.dispo.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: AlertCircle, text: t('showcase.dispo.feature1') },
                  { icon: TrendingUp, text: t('showcase.dispo.feature2') },
                  { icon: Target, text: t('showcase.dispo.feature3') },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-red-400" />
                    </div>
                    <span className="text-gray-300 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Show Details Button */}
              <motion.button
                onClick={() => setIsDispoModalOpen(true)}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{t('showcase.dispo.showDetails')}</span>
                <AlertCircle className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>

            {/* Phone mockup */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <PhoneMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: Charts & Analytics */}
      <section className="py-12 lg:py-16 bg-black overflow-hidden relative">
        {/* Gradient orb */}
        <div className="absolute top-1/2 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 60%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Phone mockup - Left side on desktop */}
            <motion.div
              className="relative lg:order-1 order-2"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <ChartPhoneMockup />
            </motion.div>

            {/* Content - Right side on desktop */}
            <motion.div
              className="lg:order-2 order-1"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
                <BarChart3 className="h-4 w-4 text-blue-400" />
                <span className="text-gray-300 text-sm">{t('showcase.charts.badge')}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {t('showcase.charts.title')}
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                  {t('showcase.charts.titleGradient')}
                </span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('showcase.charts.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: BarChart3, text: t('showcase.charts.feature1') },
                  { icon: Wallet, text: t('showcase.charts.feature2') },
                  { icon: TrendingUp, text: t('showcase.charts.feature3') },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-blue-400" />
                    </div>
                    <span className="text-gray-300 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Show Details Button */}
              <motion.button
                onClick={() => setIsChartsModalOpen(true)}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{t('showcase.charts.showDetails')}</span>
                <BarChart3 className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2.5: AzrefyPoints */}
      <section className="py-12 lg:py-16 bg-black overflow-hidden relative">
        {/* Gradient orb */}
        <div className="absolute top-1/2 left-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,45,135,0.1) 0%, transparent 60%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
                <Gift className="h-4 w-4 text-pink-400" />
                <span className="text-gray-300 text-sm">{t('showcase.points.badge')}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {t('showcase.points.title')}
                <br />
                <span className="bg-gradient-to-r from-pink-400 via-fuchsia-400 to-purple-400 bg-clip-text text-transparent">
                  {t('showcase.points.titleGradient')}
                </span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('showcase.points.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: Wallet, text: t('showcase.points.feature1') },
                  { icon: TrendingUp, text: t('showcase.points.feature2') },
                  { icon: Award, text: t('showcase.points.feature3') },
                  { icon: Gift, text: t('showcase.points.feature4') },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-pink-400" />
                    </div>
                    <span className="text-gray-300 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Show Details Button */}
              <motion.button
                onClick={() => setIsPointsModalOpen(true)}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{t('showcase.points.showDetails')}</span>
                <Gift className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>

            {/* Phone mockup */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <PointsPhoneMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 3: AI Tips & Insights */}
      <section className="py-12 lg:py-16 bg-black overflow-hidden relative">
        {/* Gradient orb */}
        <div className="absolute top-1/2 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.1) 0%, transparent 60%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Phone mockup - Left side on desktop */}
            <motion.div
              className="relative lg:order-1 order-2"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <TipsPhoneMockup />
            </motion.div>

            {/* Content - Right side on desktop */}
            <motion.div
              className="lg:order-2 order-1"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
                <Sparkles className="h-4 w-4 text-purple-400" />
                <span className="text-gray-300 text-sm">{t('showcase.tips.badge')}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {t('showcase.tips.title')}
                <br />
                <span className="bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 bg-clip-text text-transparent">
                  {t('showcase.tips.titleGradient')}
                </span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('showcase.tips.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: Sparkles, text: t('showcase.tips.feature1') },
                  { icon: CreditCard, text: t('showcase.tips.feature2') },
                  { icon: PiggyBank, text: t('showcase.tips.feature3') },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-purple-400" />
                    </div>
                    <span className="text-gray-300 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Show Details Button */}
              <motion.button
                onClick={() => setIsAITipsModalOpen(true)}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{t('showcase.tips.showDetails')}</span>
                <Sparkles className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 4: Roast Mode AI */}
      <section className="py-12 lg:py-16 bg-black overflow-hidden relative">
        {/* Gradient orb */}
        <div className="absolute top-1/2 left-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(249,115,22,0.1) 0%, transparent 60%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
                <Flame className="h-4 w-4 text-orange-400" />
                <span className="text-gray-300 text-sm">{t('showcase.roast.badge')}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {t('showcase.roast.title')}
                <br />
                <span className="bg-gradient-to-r from-orange-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">
                  {t('showcase.roast.titleGradient')}
                </span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('showcase.roast.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: Sparkles, text: t('showcase.roast.feature1') },
                  { icon: Flame, text: t('showcase.roast.feature2') },
                  { icon: Target, text: t('showcase.roast.feature3') },
                  { icon: TrendingUp, text: t('showcase.roast.feature4') },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-orange-400" />
                    </div>
                    <span className="text-gray-300 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Show Details Button */}
              <motion.button
                onClick={() => setIsRoastModalOpen(true)}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{t('showcase.roast.showDetails')}</span>
                <Flame className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>

            {/* Phone mockup */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <RoastPhoneMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 5: Money Habits Score */}
      <section className="py-12 lg:py-16 bg-black overflow-hidden relative">
        {/* Gradient orb */}
        <div className="absolute top-1/2 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, transparent 60%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Phone mockup - Left side on desktop */}
            <motion.div
              className="relative lg:order-1 order-2"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <HabitsScorePhoneMockup />
            </motion.div>

            {/* Content - Right side on desktop */}
            <motion.div
              className="lg:order-2 order-1"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
                <Award className="h-4 w-4 text-blue-400" />
                <span className="text-gray-300 text-sm">{t('showcase.habits.badge')}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {t('showcase.habits.title')}
                <br />
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">
                  {t('showcase.habits.titleGradient')}
                </span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('showcase.habits.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: BarChart3, text: t('showcase.habits.feature1') },
                  { icon: Target, text: t('showcase.habits.feature2') },
                  { icon: Award, text: t('showcase.habits.feature3') },
                  { icon: Sparkles, text: t('showcase.habits.feature4') },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-blue-400" />
                    </div>
                    <span className="text-gray-300 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Show Details Button */}
              <motion.button
                onClick={() => setIsHabitsModalOpen(true)}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-blue-500 to-teal-500 hover:from-blue-600 hover:to-teal-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{t('showcase.habits.showDetails')}</span>
                <BarChart3 className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 6: Financial Health Check-In */}
      <section className="py-12 lg:py-16 bg-black overflow-hidden relative">
        {/* Gradient orb */}
        <div className="absolute top-1/2 left-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] -translate-y-1/2 rounded-full" style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.1) 0%, transparent 60%)' }} />
        
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-full mb-6">
                <Heart className="h-4 w-4 text-green-400" />
                <span className="text-gray-300 text-sm">{t('showcase.checkin.badge')}</span>
              </div>
              
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
                {t('showcase.checkin.title')}
                <br />
                <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  {t('showcase.checkin.titleGradient')}
                </span>
              </h2>
              
              <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                {t('showcase.checkin.description')}
              </p>

              <div className="space-y-4">
                {[
                  { icon: Target, text: t('showcase.checkin.feature1') },
                  { icon: Flame, text: t('showcase.checkin.feature2') },
                  { icon: Heart, text: t('showcase.checkin.feature3') },
                  { icon: TrendingUp, text: t('showcase.checkin.feature4') },
                ].map((item, i) => (
                  <motion.div 
                    key={i}
                    className="flex items-center gap-4"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                  >
                    <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-green-400" />
                    </div>
                    <span className="text-gray-300 font-medium">{item.text}</span>
                  </motion.div>
                ))}
              </div>

              {/* Show Details Button */}
              <motion.button
                onClick={() => setIsCheckInModalOpen(true)}
                className="mt-8 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2 group"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span>{t('showcase.checkin.showDetails')}</span>
                <Heart className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
            </motion.div>

            {/* Phone mockup */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <HealthCheckPhoneMockup />
            </motion.div>
          </div>
        </div>
      </section>

      {/* All Modals */}
      <DispoDetailsModal 
        isOpen={isDispoModalOpen} 
        onClose={() => setIsDispoModalOpen(false)} 
      />
      <ChartsDetailsModal 
        isOpen={isChartsModalOpen} 
        onClose={() => setIsChartsModalOpen(false)} 
      />
      <AITipsDetailsModal 
        isOpen={isAITipsModalOpen} 
        onClose={() => setIsAITipsModalOpen(false)} 
      />
      <RoastModeDetailsModal 
        isOpen={isRoastModalOpen} 
        onClose={() => setIsRoastModalOpen(false)} 
      />
      <HabitsScoreDetailsModal 
        isOpen={isHabitsModalOpen} 
        onClose={() => setIsHabitsModalOpen(false)} 
      />
      <CheckInDetailsModal 
        isOpen={isCheckInModalOpen} 
        onClose={() => setIsCheckInModalOpen(false)} 
      />
      <PointsDetailsModal 
        isOpen={isPointsModalOpen} 
        onClose={() => setIsPointsModalOpen(false)} 
      />
    </>
  )
}

export default AppShowcaseSections
